
import {
  Component,
  Provide,
  Prop,
  Watch,
  Mixins
} from "vue-property-decorator";
import { CreateElement } from "vue";
import _ from "@/boot/lodash";

// Mixins
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import MultilevelCategoriesFetcher from "@/components/app/admin/catalogue/products/multilevelCategories/multilevelCategoriesFetchMixin";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { CatalogueProductFilters as productsFilters } from "@/data/filters/products";
import { productsSorters } from "@/data/sorters/products";
import { Sorters } from "@/data/table";
import { ProductTypes } from "@/data/enums/products";

// Typings
import {
  IConditionProductsProviderData,
  IConditionProductsProviderMethods
} from "@/models/providers/affiliateTierConditions";
import { IAffiliateTier, IAffiliateTierCondition } from "@/models/affiliate";
import { ISelectedProduct } from "@/models/products";
import { IFilter, ISorter } from "@/models/tables";
import { ITableConfig } from "@/models/tables";

@Component
export default class ConditionProductsProvider extends Mixins(
  FilteringSortingTableStore,
  MultilevelCategoriesFetcher
) {
  @Prop({ required: true })
  public affiliateTierCondition!: IAffiliateTierCondition;

  @Prop({ required: true })
  public affiliateTierId!: IAffiliateTier["id"];

  @Prop({ default: () => [] })
  public selectedProducts!: ISelectedProduct[];

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  // Data
  public dataModule: DataModules =
    DataModules.AFFILIATES_TIERS_CONDITIONS_PRODUCTS;

  public filters: IFilter[] = [];
  public sorters: ISorter[] = [];

  public defaults: ITableConfig = {
    sort: {
      field: "created_at",
      direction: Sorters.ASCENDING
    }
  };

  public isLoading = false;
  public isReloading = false;
  public hasRequestError = false;

  // Lifecycle methods

  public async created() {
    if (!this.contentIsAvailable) return;

    this.table.loading = true;
    this.isLoading = true;
    this.setApiProps();
    await this.getRootCategories(this.isReloading);
    this.setFiltersAndSorters();
    this.getData();
  }

  public guardFunctionalities = ["staff_list_products"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Methods

  public setFiltersAndSorters() {
    this.filters = this.getProductCategoryFilledFilters(
      productsFilters(),
      false
    );
    this.sorters = _.filter(
      productsSorters,
      productsSorter => productsSorter.field !== ""
    );
  }

  public manageSelectedProducts(
    selectedProducts: ISelectedProduct[],
    events: any
  ) {
    this.$store.dispatch(
      `data/${DataModules.CATALOGUE}/openSelectProductsModal`,
      {
        props: {
          productTypes: [
            ProductTypes.SINGLE_PRODUCT,
            ProductTypes.PRODUCT_BUNDLE
          ],
          selectedProducts,
          disabledProducts: [],
          isMultiselect: true
        },
        events
      }
    );
  }

  public setApiProps() {
    this.apiProps = {
      "filter[id|like]": _.map(
        this.selectedProducts,
        selectedProoduct => selectedProoduct.id
      ).join(","),
      with: "image"
    };
  }

  // Watchers

  @Watch("selectedProducts", { deep: true })
  public onSelecProductIdsChange() {
    this.setApiProps();
    this.getData({ ignoreStored: true });
  }

  // Life-cycle Methods
  public render(h: CreateElement) {
    if (!this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading },
        on: { click: () => this.getData({ ignoreStored: true }) }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $conditionProductsData: this.providerData(),
        $conditionProductsMethods: this.providerMethods
      })
    );
  }

  // Provide

  @Provide("conditionProductsProviderData")
  public providerData(): IConditionProductsProviderData {
    return {
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      enablePagination: this.enablePagination,
      activeFilters: this.activeFilters,
      filters: this.filters,
      sorters: this.sorters,
      table: this.table
    };
  }

  @Provide("conditionProductsProviderMethods")
  public get providerMethods(): IConditionProductsProviderMethods {
    return {
      getData: this.getData,
      onFiltersChange: this.onFiltersChange,
      onSort: this.onSort,
      onPageChange: this.onPageChange,
      manageSelectedProducts: this.manageSelectedProducts
    };
  }
}
